import type { Track } from '@/components/marketing/ShowSpine';
import { media, roomFrames } from './media';

/** The fan's next move, one per frame of the show-night sequence. */
const ROOM_STEPS = ['Send it', 'Spend 75 Cheer on Fireworks'];

/* ==========================================================================
   The two tracks of the home page's show spine: the same show, told by
   whoever is booking it. A venue booker (persona 1) and an artist booking
   their own shows (persona 2) do the same jobs in the same order, so the
   stops line up and only the words and the screens change.

   Every line is a shipped feature, checked in code
   (docs/plans/MARKETING_SITE.md). Deliberately not said for artists: that a
   venue they add without a Showliner account brings house currency to the
   room. Their own blackouts do raise a warning since #864, so the artist
   track may now say so.
   ========================================================================== */

export const tracks: Track[] = [
  {
    id: 'venue',
    choice: 'I book for a venue',
    stops: [
      {
        when: 'Three weeks out',
        title: 'Put Friday on the calendar',
        body: "Add the date and the acts. Most of them won't be on Showliner, and that's fine: you type the name.",
        visual: { kind: 'media', media: media.adminEventList, frame: 'browser', caption: 'Your shows and the month at a glance' },
      },
      {
        when: 'Two weeks out',
        title: 'Send each act a link',
        body: 'By text, by email, or in the group chat. They open it on their phone and say yes or no without making an account, and if there is money on it, the terms are right there.',
        visual: { kind: 'media', media: media.phoneInvite, frame: 'phone', caption: "The invite, on the act's phone" },
      },
      {
        when: 'The week of',
        title: 'Stop chasing load-in times',
        body: 'Post load-in, doors and parking once, and ask every act for their input list and how many people they are bringing. The show page shows who still owes you an answer.',
        visual: { kind: 'media', media: media.adminEventDetail, frame: 'browser', caption: 'Two party sizes still outstanding' },
      },
      {
        when: 'Friday, 6:15 PM',
        title: 'Soundcheck',
        body: "The engineer has each act's stage plot drawn on top of your house backline, with the input list, on a tablet at the board.",
        visual: { kind: 'media', media: media.adminStagePlot, frame: 'browser', caption: 'The house backline, in orange' },
      },
      {
        when: 'Friday, 8:04 PM',
        title: 'The TV runs the room',
        body: 'Fans scan the code on the screen and they are in, with no app and no account. They chat, tip the act, request songs, and spend what they earn on effects. Step through it.',
        visual: { kind: 'room', frames: roomFrames, steps: ROOM_STEPS },
      },
      {
        when: 'Friday, 11:58 PM',
        title: 'Settle up',
        body: 'Enter what the door did. Showliner works out what each act is owed from the terms you agreed, and they sign off on their phone. You pay them however you already do.',
        visual: { kind: 'media', media: media.phoneSettlement, frame: 'phone', caption: "The settlement, on the act's phone" },
      },
    ],
  },
  {
    id: 'artist',
    choice: "I'm an artist booking my own shows",
    stops: [
      {
        when: 'A month out',
        title: 'Put the run on your calendar',
        body: "Three dates, three rooms. Add the venue even if it isn't on Showliner, and block out the weekend you can't play.",
        visual: { kind: 'media', media: media.artistEventList, frame: 'browser', caption: "The band's own shows" },
      },
      {
        when: 'Three weeks out',
        title: 'Ask the band you want opening',
        body: "Send them a link by text, by email, or in the group chat. They say yes on their phone without an account, and if there's money on it, the terms are written down.",
        visual: { kind: 'media', media: media.artistPhoneInvite, frame: 'phone', caption: "The invite, on the opener's phone" },
      },
      {
        when: 'The week of',
        title: 'Get the details from everyone',
        body: "Post load-in and doors once, tell the opener what backline you're bringing, and ask how many people they're bringing. The show page shows what's still outstanding.",
        visual: { kind: 'media', media: media.artistEventDetail, frame: 'browser', caption: 'A venue that isn\'t on Showliner, and an opener who still owes an answer' },
      },
      {
        when: 'Load-in',
        title: 'Your stage plot, already drawn',
        body: "Save it once as a template and bring it to every room. If the venue drew its backline, yours sits on top of it, and it exports as a PDF for the ones that didn't.",
        visual: { kind: 'media', media: media.adminStagePlot, frame: 'browser', caption: "An act's plot on the venue's backline" },
      },
      {
        when: '9:12 PM',
        title: 'Bring the room with you',
        body: 'No TV at The Lantern? Put your QR code on the merch table. Fans scan it and land in your room, where they chat, tip you, and earn Cheer that follows them to your next show. Step through it.',
        visual: { kind: 'room', frames: roomFrames, steps: ROOM_STEPS },
      },
      {
        when: '12:20 AM',
        title: 'Settle up with the opener',
        body: 'Enter what the door did. Showliner works out what Low Tide is owed from the terms you agreed, and they sign off on their phone.',
        visual: { kind: 'media', media: media.artistPhoneSettlement, frame: 'phone', caption: "The settlement, on the opener's phone" },
      },
    ],
  },
];
